import moment from 'moment';
import { IAirspaceClassification } from '../authority-tiles/IAirspaceProperties';
import { ILAANCEanbledFor } from '../authority-tiles/ILAANCProperties';
import { IAdvisoryTypeV3 } from './IAdvisoryPropertiesV3';
import { IPersistableAuthorizableLAANCAdvisoryProperties, ILAANCStatus, IPersistableLAANCStatus, IFAALAANCStatus, ILAANCStatusHistory, ILAANCStatusMeta } from './IAuthorizableLAANCAdvisoryProperties';

// in the ctor we may or may not get laancStatusHistory (eg a newly initialized authorization won't have one yet)
type IAuthorizableLAANCAdvisoryPropertiesCtorProperties = Omit<IPersistableAuthorizableLAANCAdvisoryProperties, 'laancStatusHistory'>
    & { laancStatusHistory?: ILAANCStatusHistory[] };

export class AuthorizableLAANCAdvisoryProperties implements Omit<IPersistableAuthorizableLAANCAdvisoryProperties, 'laancStatus' | 'laancStatusHistory'> {
    source: 'uasfm+authorizable' = 'uasfm+authorizable';
    operationID: string;
    airspaceIdent: string;
    uasfmGridIds: string[];
    uasfmGridIdsV2: { gridId: string; lastEdited: string; fromPadding?: boolean; }[];
    airspaceClasses: IAirspaceClassification[];
    maxAltitude: number;
    UASFM_Enabled: ILAANCEanbledFor[];
    advisoryType: IAdvisoryTypeV3;
    startTime: string;
    endTime: string;
    isLicensed: boolean;

    private laancStatus?: IPersistableLAANCStatus;
    private laancStatusHistory: ILAANCStatusHistory[];

    constructor(props: IAuthorizableLAANCAdvisoryPropertiesCtorProperties, initializedStatusMeta?: ILAANCStatusMeta) {
        // laanc status is crucial to get the right answer to the user (can you fly or not?)
        // therefore lets throw if we a status we don't expect, rather than risk getting
        // the answer wrong
        if (props.laancStatus && !validPersistedStatusList.includes(props.laancStatus)) {
            throw new Error(`got status ${props.laancStatus} which is not a supported status. Expected one of ${validPersistedStatusList.join(', ')}`);
        }

        if (!props.laancStatusHistory) {
            if (!initializedStatusMeta?.initializedSource) {
                throw new Error('props.laancStatusHistory is undefined but initializedStatusMeta.initializedSource was not provided');
            }
            this.laancStatusHistory = [{ status: 'initialized', timestamp: new Date().toISOString(), statusMeta: initializedStatusMeta }];
        } else {
            this.laancStatusHistory = props.laancStatusHistory;
        }

        this.operationID = props.operationID;
        this.airspaceIdent = props.airspaceIdent;

        // TODO: deprecation plan
        this.uasfmGridIds = props.uasfmGridIds || [];
        this.uasfmGridIdsV2 = props.uasfmGridIdsV2 || this.uasfmGridIds.map(compositeGridId => {
            const [gridId, lastEdited] = compositeGridId.split(':');
            return{ gridId, lastEdited };
        });

        this.airspaceClasses = props.airspaceClasses;
        this.maxAltitude = props.maxAltitude;
        this.UASFM_Enabled = props.UASFM_Enabled;
        this.laancStatus = props.laancStatus;
        this.advisoryType = props.advisoryType;
        this.startTime = props.startTime;
        this.endTime = props.endTime;
        this.isLicensed = props.isLicensed;
    }

    getAPLAANCStatus = ({ endTime }: { endTime: string }): ILAANCStatus => {
        const isInPast = Date.now() > moment(endTime).valueOf();

        if (isInPast && (!this.laancStatus || this.laancStatus === 'authorizing')) {
            return 'never-authorized';
        }

        if (!this.laancStatus) {
            return 'initialized';
        }

        return persistableStatusToLAANCStatusMap[this.laancStatus];
    }

    getLAANCStatus = ({ endTime }: { endTime: string }): IFAALAANCStatus => {
        const isInPast = Date.now() > moment(endTime).valueOf();

        const status = this.getAPLAANCStatus({ endTime });

        if (status === 'rescinded' && isInPast) {
            return 'De-Authorized';
        }
        if (status === 'authorized' && isInPast) {
            return 'Complete';
        }
        if (status === 'invalidated' && isInPast){
            return 'Complete';
        }

        return apLAANCStatusToFAAMap[status];
    }

    getLAANCStatusHistory = (): Readonly<ILAANCStatusHistory>[] => {
        return this.laancStatusHistory;
    }

    setAPLAANCStatus = ({ toStatus, endTime, timestamp, statusMeta }: { toStatus: IPersistableLAANCStatus, endTime: string, timestamp?: string, statusMeta?: ILAANCStatusMeta }) => {
        if (!timestamp) {
            timestamp = new Date().toISOString();
        }

        const currentStatus = this.getAPLAANCStatus({ endTime });
        const hasEnded = moment(endTime).isBefore(timestamp);

        // time-based case that's not easily handled with stateTransition map is that an "authorized" advisory can be deleted if it has ended
        const allowedDelete = (hasEnded && currentStatus === 'authorized' && toStatus === 'user-deleted');

        if (!allowedDelete && hasEnded && terminalAfterEnded.includes(currentStatus)) {
            throw new Error(`attempted to go from ${currentStatus} to ${toStatus} but the mission has ended, therefore this is not allowed`);
        }

        if (!allowedDelete && !validStateTransitions[this.getAPLAANCStatus({ endTime })].includes(toStatus)) {
            throw new Error(`attempted to go from ${this.getAPLAANCStatus({ endTime })}(${this.laancStatus} ${endTime} at ${timestamp}) to ${toStatus} which is not allowed`);
        }

        const fromStatus = this.laancStatus;
        this.laancStatus = toStatus;

        const message = JSON.stringify({ fromStatus, endTime });

        this.laancStatusHistory.push({ status: toStatus, timestamp, message, statusMeta: statusMeta || {} });
    }

    updateLAANCStatusTimestamp = ({ status, timestamp }) => {
        const item = this.laancStatusHistory.find(h => h.status === status);
        if (!item) {
            throw new Error(`could not find status "${status}" in laancHistory for operation ${this.operationID} to update timestamp`);
        }
        item.message = JSON.stringify({ originalTimestamp: item.timestamp, ...JSON.parse(item.message || '{}') });
        item.timestamp = timestamp;
    }
}


const apLAANCStatusToFAAMap: { [key in ILAANCStatus]: IFAALAANCStatus } = {
    'rescinded': 'Rescinded_Awaiting',
    'rescind-acked': 'De-Authorized',
    'never-authorized': 'Never Authorized',
    'authorizing': 'Pending',
    'authorizing-failed': 'Never Authorized',
    'authorized': 'Authorized',
    'initialized': 'initialized',
    'user-canceled': 'De-Authorized',
    'invalidated': 'Authorized',
    'invalidated-acked': 'De-Authorized',
    'user-closed': 'Complete',
    'user-deleted': 'De-Authorized'
};

const persistableStatusToLAANCStatusMap: { [key in IPersistableLAANCStatus]: ILAANCStatus } = {
    'user-canceled-pre-authorized': 'never-authorized',
    'user-canceled': 'user-canceled',
    'rescinded': 'rescinded',
    'rescind-acked': 'rescind-acked',
    'authorized': 'authorized',
    'authorizing': 'authorizing',
    'authorizing-failed': 'authorizing-failed',
    'invalidated': 'invalidated',
    'invalidated-acked': 'invalidated-acked',
    'user-closed': 'user-closed',
    'user-deleted': 'user-deleted'
};

const validStateTransitions: { [key in ILAANCStatus]: IPersistableLAANCStatus[] } = {
    'initialized': ['authorizing', 'user-canceled-pre-authorized'],
    'authorizing': [
        'authorized',
        'authorizing-failed',

        // we are not letting you cancel, until we add support for waiting til we get the FAA response
        // to send the cancel
        // 'user-canceled-pre-authorized'
    ],
    'authorizing-failed': [],
    'authorized': ['rescinded', 'user-canceled', 'invalidated', 'user-closed'],
    'user-canceled': [ 'user-deleted' ],
    'rescinded': ['rescind-acked'],
    'rescind-acked': [ 'user-deleted' ],
    'never-authorized': [],
    'invalidated': [ 'invalidated-acked', 'rescinded' ],
    'invalidated-acked': [ 'user-deleted' ],
    'user-closed': [ 'user-deleted' ],
    'user-deleted': []
};

const terminalAfterEnded: ILAANCStatus[] = ['authorized', 'authorizing-failed', 'rescinded', 'invalidated'];

const validStatusMap: { [key in IPersistableLAANCStatus]: true } = {
    'authorizing': true,
    'authorizing-failed': true,
    'authorized': true,
    'rescinded': true,
    'user-canceled': true,
    'user-canceled-pre-authorized': true,
    'rescind-acked': true,
    'invalidated': true,
    'invalidated-acked': true,
    'user-closed': true,
    'user-deleted': true
};
const validPersistedStatusList: IPersistableLAANCStatus[] = Object.keys(validStatusMap) as IPersistableLAANCStatus[];
